import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";

export const SOUNDS = [
  { id: 1, name: "Win Celebration 01", src: "/sounds/win-celebration-01.mp3" },
  { id: 2, name: "Win Celebration 02", src: "/sounds/win-celebration-02.mp3" },
  { id: 3, name: "Win Celebration 03", src: "/sounds/win-celebration-03.mp3" },
  { id: 4, name: "Win Celebration 04", src: "/sounds/win-celebration-04.mp3" },
  { id: 5, name: "Win Celebration 05", src: "/sounds/win-celebration-05.mp3" },
  { id: 6, name: "Alert Sound 01", src: "/sounds/ai-music-01.mp3" },
  { id: 7, name: "Alert Sound 02", src: "/sounds/ai-music-02.mp3" },
  { id: 8, name: "Alert Sound 03", src: "/sounds/ai-music-03.mp3" },
  { id: 9, name: "Alert Sound 04", src: "/sounds/ai-music-04.mp3" },
  { id: 10, name: "Alert Sound 05", src: "/sounds/ai-music-05.mp3" },
];

const load = (k, d) => { try { const v = JSON.parse(localStorage.getItem(k)); return v ?? d; } catch { return d; } };
const save = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* ignore */ } };

const SoundContext = createContext(null);

export function SoundProvider({ children }) {
  const audioRef = useRef(null);
  const [aiSoundOn, setAiSoundOn] = useState(() => load("sfx_ai_sound_on", true));
  const [autoMode, setAutoMode] = useState(() => load("sfx_ai_auto_sound", true));
  const [current, setCurrent] = useState(() => load("sfx_sound_idx", 2));
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(() => load("sfx_sound_vol", 0.8));
  const [playerOpen, setPlayerOpen] = useState(false);
  const [botId, setBotId] = useState(null);

  useEffect(() => save("sfx_ai_sound_on", aiSoundOn), [aiSoundOn]);
  useEffect(() => save("sfx_ai_auto_sound", autoMode), [autoMode]);
  useEffect(() => save("sfx_sound_idx", current), [current]);
  useEffect(() => save("sfx_sound_vol", volume), [volume]);
  useEffect(() => { if (audioRef.current) audioRef.current.volume = volume; }, [volume]);

  // Hydrate the saved winning-sound preference from the client's account (backend),
  // so the choice follows the client across devices/sessions — not just this browser.
  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const { data } = await api.get("/bots");
        const bot = Array.isArray(data) ? data[0] : null;
        if (!on || !bot) return;
        setBotId(bot.id);
        if (Number.isInteger(bot.win_sound_idx)) setCurrent(bot.win_sound_idx);
        if (typeof bot.sound_enabled === "boolean") setAiSoundOn(bot.sound_enabled);
        if (typeof bot.auto_sound === "boolean") setAutoMode(bot.auto_sound);
      } catch { /* ignore */ }
    })();
    return () => { on = false; };
  }, []);

  const persistSound = useCallback(async () => {
    if (!botId) return false;
    try {
      await api.post(`/bots/${botId}/settings`, {
        win_sound_idx: current, sound_enabled: aiSoundOn, auto_sound: autoMode,
      });
      return true;
    } catch {
      return false;
    }
  }, [botId, current, aiSoundOn, autoMode]);

  const playIndex = useCallback((i) => {
    const idx = ((i % SOUNDS.length) + SOUNDS.length) % SOUNDS.length;
    setCurrent(idx);
    const a = audioRef.current;
    if (!a) return;
    a.src = SOUNDS[idx].src;
    a.volume = volume;
    a.play().then(() => { setPlaying(true); setPlayerOpen(true); }).catch(() => {});
  }, [volume]);

  const play = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    setPlayerOpen(true);
    if (!a.src) { playIndex(current); return; }
    a.play().then(() => setPlaying(true)).catch(() => {});
  }, [current, playIndex]);

  const pause = useCallback(() => { audioRef.current?.pause(); setPlaying(false); }, []);
  const toggle = useCallback(() => { (playing ? pause() : play()); }, [playing, play, pause]);
  const next = useCallback(() => playIndex(current + 1), [current, playIndex]);
  const prev = useCallback(() => playIndex(current - 1), [current, playIndex]);
  const preview = useCallback((i) => playIndex(typeof i === "number" ? i : current), [current, playIndex]);

  const playCelebration = useCallback(() => {
    if (!aiSoundOn) return;
    const i = autoMode ? Math.floor(Math.random() * SOUNDS.length) : current;
    playIndex(i);
  }, [aiSoundOn, autoMode, current, playIndex]);

  // Auto Alert Mode — celebrate on realized profit increase (winning trade / milestone).
  const prevProfit = useRef(null);
  useEffect(() => {
    if (!aiSoundOn || !autoMode) return;
    let on = true;
    const check = async () => {
      try {
        const { data } = await api.get("/account");
        const p = Number(data?.propfirm_snapshot?.profit ?? data?.profit ?? NaN);
        if (Number.isNaN(p)) return;
        if (prevProfit.current !== null && p > prevProfit.current + 0.01) playCelebration();
        prevProfit.current = p;
      } catch { /* ignore */ }
    };
    check();
    const t = setInterval(() => { if (on) check(); }, 30000);
    return () => { on = false; clearInterval(t); };
  }, [aiSoundOn, autoMode, playCelebration]);

  const value = {
    SOUNDS, aiSoundOn, setAiSoundOn, autoMode, setAutoMode,
    current, playing, volume, setVolume, playerOpen, setPlayerOpen,
    play, pause, toggle, next, prev, preview, playIndex, playCelebration,
    persistSound,
    currentSound: SOUNDS[current],
  };

  return (
    <SoundContext.Provider value={value}>
      {children}
      <audio ref={audioRef} onEnded={() => setPlaying(false)} onPause={() => setPlaying(false)} onPlay={() => setPlaying(true)} preload="none" />
    </SoundContext.Provider>
  );
}

export const useSound = () => useContext(SoundContext);
